import { motion } from "framer-motion";
import { BlogPost } from "@/data/blogData";
import { BlogCard } from "./BlogCard";
import { useEffect, useState, useMemo } from "react";

interface BlogMasonryGridProps {
  posts: BlogPost[];
  onPostClick: (slug: string) => void;
}

export function BlogMasonryGrid({ posts, onPostClick }: BlogMasonryGridProps) {
  const [columns, setColumns] = useState(3);

  // Responsive column count
  useEffect(() => {
    const updateColumns = () => {
      if (window.innerWidth < 640) {
        setColumns(1);
      } else if (window.innerWidth < 1024) {
        setColumns(2);
      } else {
        setColumns(3);
      }
    };

    updateColumns();
    window.addEventListener("resize", updateColumns);
    return () => window.removeEventListener("resize", updateColumns);
  }, []);

  // Distribute posts into columns for masonry effect
  const columnPosts = useMemo(() => {
    const cols: BlogPost[][] = Array.from({ length: columns }, () => []);

    posts.forEach((post, index) => {
      cols[index % columns].push(post);
    });

    return cols;
  }, [posts, columns]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1,
      },
    },
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="flex gap-6"
    >
      {columnPosts.map((colPosts, colIndex) => (
        <div key={colIndex} className="flex-1 flex flex-col gap-6">
          {colPosts.map((post, postIndex) => (
            <BlogCard
              key={post.id}
              post={post}
              index={colIndex * 3 + postIndex}
              variant="default"
              onClick={() => onPostClick(post.slug)}
            />
          ))}
        </div>
      ))}
    </motion.div>
  );
}
